// /app/posts — Workspace-wide posts list with lifecycle status + actions.
//
// Single surface to answer: "what did I generate, what's queued, what
// published, what failed". Status filter chips, per-row actions:
//   - Schedule / reschedule  -> date-time picker
//   - Cancel (queued)        -> back to draft
//   - Delete                 -> remove
//   - Open                   -> opens PostDrawer for full edit
//
// Backend endpoints:
//   GET    /v1/posts?status=&brand=&limit=
//   POST   /v1/posts/:id/schedule  { scheduled_for }
//   POST   /v1/posts/:id/cancel
//   DELETE /v1/posts/:id

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";
import { PageHead } from "@/components/primitives";
import { Icons } from "@/components/icons";
import { useBrands } from "@/hooks/use-persona";
import { usePostDrawer } from "@/components/PostDrawer";
import { resolveImageUrl } from "@/lib/asset-url";
import type { Post } from "@/lib/types";

export const Route = createFileRoute("/app/posts")({
  component: PostsPage,
});

// "draft" in the UI = either an unposted post row (status='draft') OR a
// pending generation_version (the variant the operator generated but
// hasn't picked yet). Both represent "I generated this and paid credits,
// I should finish it" so we merge them under the same filter.
type StatusFilter =
  | "all"
  | "draft"
  | "scheduled"
  | "publishing"
  | "published"
  | "failed"
  | "paused"
  | "cancelled";

const STATUS_META: Record<
  Exclude<StatusFilter, "all">,
  { tr: string; en: string; color: string }
> = {
  draft:      { tr: "Taslak",       en: "Draft",      color: "#F59E0B" },
  scheduled:  { tr: "Planlandı",    en: "Scheduled",  color: "#3B82F6" },
  publishing: { tr: "Yayınlanıyor", en: "Publishing", color: "#A855F7" },
  published:  { tr: "Yayında",      en: "Published",  color: "#10B981" },
  failed:     { tr: "Başarısız",    en: "Failed",     color: "#EF4444" },
  // Parked because the social account disconnected — auto-resumes on reconnect.
  paused:     { tr: "Duraklatıldı", en: "Paused",     color: "#F97316" },
  cancelled:  { tr: "İptal",        en: "Cancelled",  color: "#6B7280" },
};

// Approval rows are generation_versions waiting for the operator to
// pick. They live in a different table from posts but share most of
// the same display shape, so we render them in the same list.
interface ApprovalRow {
  id: string;            // version id (NOT post id)
  generation_id: string;
  brand_id: string;
  platform: string;
  text_body?: string | null;
  hashtags?: string[] | null;
  image_url?: string | null;
  created_at: string;
  prompt?: string;
}

function PostsPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const qc = useQueryClient();
  const openDrawer = usePostDrawer((s) => s.open);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [brandFilter, setBrandFilter] = useState<string>("");

  const { data: brandsData } = useBrands();
  const brands = brandsData?.data ?? [];

  // Posts table: everything that's been approved into a post row.
  //
  // 2026-08-07 "my posts disappeared": GET /v1/posts orders
  // `COALESCE(scheduled_for, created_at) ASC` and truncates at LIMIT, so
  // asking for 100 rows out of 190 returned the OLDEST 100 — freshly
  // approved drafts were never in the payload at all. Ask for the
  // backend max (500) and sort newest-first client-side so the thing you
  // just made is the first thing you see.
  const wantPosts = true; // always fetch — used by every filter except possibly draft-only
  const { data, isLoading } = useQuery({
    queryKey: ["posts", "list", { statusFilter, brandFilter }],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (statusFilter !== "all") qs.set("status", statusFilter);
      if (brandFilter) qs.set("brand", brandFilter);
      qs.set("limit", "500");
      const r = await api<{ data: Post[] }>(`/posts?${qs.toString()}`);
      return r.data ?? [];
    },
    enabled: wantPosts,
    staleTime: 15_000,
  });
  // Newest first, mirroring the backend's COALESCE(scheduled_for,
  // created_at) key so undated drafts sort by when they were generated.
  const posts = useMemo(
    () =>
      (data ?? [])
        .slice()
        .sort((a, b) => postSortKey(b).localeCompare(postSortKey(a))),
    [data],
  );

  // Pending generation_versions live in a separate table. Surface them
  // under the "Taslak / Draft" filter (and on "All") because in the
  // operator's mental model they're unfinished drafts — they cost
  // credits to generate and we don't want them lost.
  const wantApprovals =
    statusFilter === "all" || statusFilter === "draft";
  const { data: approvalsData, isLoading: approvalsLoading } = useQuery({
    queryKey: ["approvals", "list", { brandFilter }],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (brandFilter) qs.set("brand", brandFilter);
      qs.set("limit", "100");
      const r = await api<{ approvals: ApprovalRow[] }>(`/approvals?${qs.toString()}`);
      return r.approvals ?? [];
    },
    enabled: wantApprovals,
    staleTime: 15_000,
  });
  const approvals = approvalsData ?? [];

  // Promote a pending version into a real post (status='draft').
  // Backend nuance: /versions/:id/accept JUST flips the version's
  // status — it does NOT create a post row. The post-creation path is
  // POST /v1/generations/:gen_id/approve with selections + save_as_draft.
  // We use approve here so the operator sees a fresh draft post in the
  // list after clicking "Postla".
  const accept = useMutation({
    mutationFn: (a: ApprovalRow) =>
      api(`/generations/${a.generation_id}/approve`, {
        method: "POST",
        body: {
          selections: { [a.platform]: a.id },
          save_as_draft: true,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["posts"] });
    },
  });
  const reject = useMutation({
    mutationFn: (versionId: string) =>
      api(`/versions/${versionId}/reject`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
  });

  const cancel = useMutation({
    mutationFn: (id: string) =>
      api(`/posts/${id}/cancel`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["posts"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/posts/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["posts"] }),
  });
  const schedule = useMutation({
    mutationFn: (input: { id: string; when: string }) =>
      api(`/posts/${input.id}/schedule`, {
        method: "POST",
        body: { scheduled_for: input.when },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["posts"] }),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "04 · Postlar" : "04 · Posts"}
        title={tr ? "Tüm üretilenler, tek listede." : "Everything you generated, in one list."}
        subtitle={
          tr
            ? "Taslak, planlandı, yayında, başarısız — durum filtresinden seç. Zamanı düzenle, kuyruktan çıkar veya tekrar taslağa al."
            : "Draft, scheduled, published, failed — filter by status. Reschedule, pull from queue, or send back to draft."
        }
      />

      <div
        className="scroll-y"
        style={{ flex: 1, padding: "16px 24px 48px", maxWidth: 1200, margin: "0 auto", width: "100%" }}
      >
        {/* Filter chips */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 14,
            alignItems: "center",
          }}
        >
          <StatusChip
            active={statusFilter === "all"}
            onClick={() => setStatusFilter("all")}
            label={tr ? "Tümü" : "All"}
            color="var(--accent)"
          />
          {(Object.keys(STATUS_META) as Array<keyof typeof STATUS_META>).map((s) => (
            <StatusChip
              key={s}
              active={statusFilter === s}
              onClick={() => setStatusFilter(s)}
              label={tr ? STATUS_META[s].tr : STATUS_META[s].en}
              color={STATUS_META[s].color}
            />
          ))}
          {brands.length > 1 ? (
            <select
              className="input"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              style={{ marginLeft: "auto", fontSize: 12, padding: "4px 8px", width: 200 }}
            >
              <option value="">{tr ? "Tüm markalar" : "All brands"}</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.trade_name}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        {/* Credit-safety banner when looking at drafts. Generation costs
            money on both sides — operator's credit and our AI bill. We
            want to make sure they don't abandon what they already paid
            to generate. */}
        {(statusFilter === "draft" || statusFilter === "all") &&
        approvals.length > 0 ? (
          <div
            style={{
              padding: "10px 14px",
              marginBottom: 10,
              borderRadius: 8,
              background: "rgba(245,158,11,0.08)",
              border: "1px solid rgba(245,158,11,0.3)",
              fontSize: 12,
              color: "var(--tx)",
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              lineHeight: 1.55,
            }}
          >
            <Icons.Sparkles size={14} style={{ color: "#F59E0B", marginTop: 1, flexShrink: 0 }} />
            <span>
              {tr
                ? `${approvals.length} taslak üretildi ve seni bekliyor. Bu üretimler için kredi kullanıldı — postlamayı unutma. Her birini kontrol et, beğendiğini "Postla"ya bas. Postla dedikten sonra gönderi zamanı seçilmemiş taslak olarak kaydedilir: yayına çıkması için Takvim'deki "Zamanlanmamış" bölümünden bir güne sürükle.`
                : `${approvals.length} drafts generated and waiting. These already cost credits — don't lose them. Review each, hit "Add as post" on what you like. That saves it as an unscheduled draft: it only publishes once you give it a time from the Calendar's Unscheduled tray.`}
            </span>
          </div>
        ) : null}

        {(wantPosts && isLoading) || (wantApprovals && approvalsLoading) ? (
          <div className="skel" style={{ height: 200, borderRadius: 12 }} />
        ) : posts.length === 0 && approvals.length === 0 ? (
          <div
            className="card"
            style={{
              padding: 40,
              textAlign: "center",
              color: "var(--mu)",
              fontSize: 14,
            }}
          >
            {tr
              ? "Bu filtre için post yok. Üret sekmesinden başla."
              : "No posts for this filter. Start from the Generate tab."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Real post rows first. Pending approvals used to lead the
                list "because they're the most actionable", but there are
                62 of them on a working account — the posts the operator
                actually added were 62 rows below the fold, which is half
                of "I added posts and they disappeared". Approvals still
                render right below, and the amber banner above announces
                their count. */}
            {wantPosts
              ? posts.map((p) => (
                  <PostRow
                    key={p.id}
                    post={p}
                    brandName={brands.find((b) => b.id === p.brand_id)?.trade_name}
                    tr={tr}
                    onOpen={() => openDrawer(p.id)}
                    onCancel={() => cancel.mutate(p.id)}
                    onDelete={() => {
                      const ok = window.confirm(
                        tr ? "Bu postu silmek istediğinden emin misin?" : "Delete this post?",
                      );
                      if (ok) del.mutate(p.id);
                    }}
                    onSchedule={(when) => schedule.mutate({ id: p.id, when })}
                    busy={cancel.isPending || del.isPending || schedule.isPending}
                  />
                ))
              : null}
            {wantApprovals
              ? approvals.map((a) => (
                  <ApprovalReviewRow
                    key={`v:${a.id}`}
                    approval={a}
                    brandName={brands.find((b) => b.id === a.brand_id)?.trade_name}
                    tr={tr}
                    onAccept={() => accept.mutate(a)}
                    onReject={() => {
                      const ok = window.confirm(
                        tr
                          ? "Bu taslağı silmek istediğine emin misin? Üretim için kredi kullanıldı, silersen geri gelmez."
                          : "Discard this draft? Credits were spent to generate it — this cannot be undone.",
                      );
                      if (ok) reject.mutate(a.id);
                    }}
                    busy={accept.isPending || reject.isPending}
                  />
                ))
              : null}
          </div>
        )}

        {(cancel.error || del.error || schedule.error) ? (
          <p style={{ color: "var(--error)", fontSize: 12, marginTop: 12 }} role="alert">
            {(cancel.error || del.error || schedule.error) instanceof ApiError
              ? ((cancel.error || del.error || schedule.error) as ApiError).message
              : tr ? "Bir şey ters gitti" : "Something went wrong"}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ApprovalReviewRow — renders a generation_version waiting for approval.
// Shape parallels PostRow but actions are Accept (→ post) / Reject.
function ApprovalReviewRow({
  approval,
  brandName,
  tr,
  onAccept,
  onReject,
  busy,
}: {
  approval: ApprovalRow;
  brandName?: string;
  tr: boolean;
  onAccept: () => void;
  onReject: () => void;
  busy: boolean;
}) {
  // Drafts don't have a post_id yet (they're generation_versions),
  // so PostDrawer can't fetch them via /posts/:id. Use a lightweight
  // local lightbox just for previewing the image + caption.
  const [preview, setPreview] = useState(false);
  const platformColor =
    {
      instagram: "#E1306C",
      facebook: "#1877F2",
      x: "#FFFFFF",
      linkedin: "#0A66C2",
      telegram: "#26A5E4",
    }[approval.platform] || "var(--accent)";

  return (
    <div
      className="card"
      style={{
        padding: 12,
        display: "flex",
        alignItems: "stretch",
        gap: 12,
        // Pending row has a subtle amber left border so it stands out.
        borderLeft: "3px solid #F59E0B",
      }}
    >
      <div
        onClick={() => approval.image_url && setPreview(true)}
        style={{
          width: 64,
          height: 64,
          borderRadius: 6,
          background: "var(--elev)",
          flexShrink: 0,
          overflow: "hidden",
          border: "1px solid var(--b1)",
          cursor: approval.image_url ? "zoom-in" : "default",
        }}
        title={approval.image_url ? (tr ? "Önizle" : "Preview") : ""}
      >
        {approval.image_url ? (
          <img
            src={resolveImageUrl(approval.image_url)}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              color: "var(--mu)",
            }}
          >
            {tr ? "Görsel yok" : "No image"}
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: platformColor,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {approval.platform}
          </span>
          {brandName ? (
            <span style={{ fontSize: 11, color: "var(--mu)" }}>· {brandName}</span>
          ) : null}
          <span
            style={{
              fontSize: 10,
              padding: "2px 8px",
              borderRadius: 999,
              background: "#F59E0B22",
              color: "#F59E0B",
              fontWeight: 500,
            }}
            title={
              tr
                ? "Üretildi, henüz postlanmadı. Postla'ya basınca yayın hattına eklenir."
                : "Generated, not posted yet. Click Add-as-post to move it into the publishing queue."
            }
          >
            {tr ? "Taslak · Üretildi" : "Draft · Generated"}
          </span>
          <span className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
            {formatDateTime(approval.created_at, tr)}
          </span>
        </div>
        <div
          onClick={() => approval.image_url && setPreview(true)}
          style={{
            fontSize: 13,
            color: "var(--tx)",
            lineHeight: 1.5,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            cursor: approval.image_url ? "pointer" : "default",
          }}
          title={approval.text_body ?? ""}
        >
          {approval.text_body || (tr ? "(başlık yok)" : "(no caption)")}
        </div>
        {approval.prompt ? (
          <div style={{ fontSize: 10, color: "var(--mu)", fontStyle: "italic" }}>
            {tr ? "İstek:" : "Prompt:"} {approval.prompt}
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          alignItems: "flex-end",
          justifyContent: "center",
        }}
      >
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={onAccept}
          disabled={busy}
          title={
            tr
              ? "Taslağı postla — zamanı seçilmemiş taslak olarak kaydedilir; Takvim'deki 'Zamanlanmamış' bölümünden bir güne sürükleyip saatini vermen gerekir."
              : "Add as post — saved as an UNSCHEDULED draft; you still have to give it a time from the Calendar's Unscheduled tray before it can publish."
          }
        >
          {tr ? "Postla" : "Add as post"}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onReject}
          disabled={busy}
          style={{ color: "var(--mu)" }}
          title={
            tr
              ? "Bu taslağı sil (üretim kredisi geri gelmez)"
              : "Discard this draft (generation credit is not refunded)"
          }
        >
          {tr ? "Sil" : "Discard"}
        </button>
      </div>

      {preview && approval.image_url ? (
        <DraftLightbox
          imageUrl={resolveImageUrl(approval.image_url) || ""}
          caption={approval.text_body || ""}
          hashtags={approval.hashtags || []}
          platform={approval.platform}
          tr={tr}
          onClose={() => setPreview(false)}
        />
      ) : null}
    </div>
  );
}

// DraftLightbox — full-screen image + caption preview for draft
// (pending) generation versions. PostDrawer can't render these
// because they don't have a post_id yet (they live in
// content.generation_versions). This is a lightweight modal that
// shows what the post will look like once approved.
function DraftLightbox({
  imageUrl,
  caption,
  hashtags,
  platform,
  tr,
  onClose,
}: {
  imageUrl: string;
  caption: string;
  hashtags: string[];
  platform: string;
  tr: boolean;
  onClose: () => void;
}) {
  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        cursor: "zoom-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 720,
          width: "100%",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          cursor: "default",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#fff",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {platform} · {tr ? "Taslak önizleme" : "Draft preview"}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={tr ? "Kapat" : "Close"}
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "#fff",
              borderRadius: 4,
              padding: "4px 10px",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            ✕
          </button>
        </div>
        <img
          src={imageUrl}
          alt=""
          style={{
            width: "100%",
            maxHeight: "60vh",
            objectFit: "contain",
            borderRadius: 8,
            background: "var(--elev)",
          }}
        />
        {caption ? (
          <div
            className="scroll-y"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              padding: 14,
              color: "#fff",
              fontSize: 14,
              lineHeight: 1.55,
              maxHeight: "20vh",
              whiteSpace: "pre-wrap",
            }}
          >
            {caption}
            {hashtags.length > 0 ? (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.6)",
                }}
              >
                {hashtags.map((h) => `#${h.replace(/^#/, "")}`).join(" ")}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusChip({
  active,
  onClick,
  label,
  color,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  color: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "5px 12px",
        borderRadius: 999,
        border: `1.5px solid ${active ? color : "var(--b2)"}`,
        background: active ? `${color}22` : "transparent",
        color: active ? color : "var(--tx2)",
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: color,
        }}
      />
      {label}
    </button>
  );
}

function PostRow({
  post,
  brandName,
  tr,
  onOpen,
  onCancel,
  onDelete,
  onSchedule,
  busy,
}: {
  post: Post;
  brandName?: string;
  tr: boolean;
  onOpen: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onSchedule: (whenISO: string) => void;
  busy: boolean;
}) {
  const meta = STATUS_META[post.status as keyof typeof STATUS_META];
  const [scheduling, setScheduling] = useState(false);
  // Pre-fill with the post's scheduled_for, or the nearest tidy
  // 15-min slot in the future so a one-click reschedule is enough.
  const [whenLocal, setWhenLocal] = useState<string>(() =>
    post.scheduled_for
      ? toLocalDatetimeInput(post.scheduled_for)
      : toLocalDatetimeInput(nextScheduleSlotISO()),
  );

  const platformColor =
    {
      instagram: "#E1306C",
      facebook: "#1877F2",
      x: "#FFFFFF",
      linkedin: "#0A66C2",
      telegram: "#26A5E4",
    }[post.platform] || "var(--accent)";

  // These MUST mirror content-service's store guards or the button renders
  // and then 409s:
  //   SchedulePost → internal/store/store.go: status IN (draft, failed, scheduled)
  //   CancelPost   → internal/store/store.go: status IN (draft, scheduled)
  // "cancelled" was in canSchedule and "publishing" in canCancel; neither is
  // accepted, so both buttons failed every time they were clicked.
  const canSchedule =
    post.status === "draft" ||
    post.status === "scheduled" ||
    post.status === "failed";
  const canCancel = post.status === "draft" || post.status === "scheduled";

  return (
    <div
      className="card"
      style={{
        padding: 12,
        display: "flex",
        alignItems: "stretch",
        gap: 12,
      }}
    >
      {/* Thumbnail or placeholder */}
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 6,
          background: "var(--elev)",
          flexShrink: 0,
          overflow: "hidden",
          border: "1px solid var(--b1)",
          cursor: "pointer",
        }}
        onClick={onOpen}
      >
        {post.image_url ? (
          <img
            src={resolveImageUrl(post.image_url)}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              color: "var(--mu)",
            }}
          >
            {tr ? "Görsel yok" : "No image"}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: platformColor,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {post.platform}
          </span>
          {brandName ? (
            <span style={{ fontSize: 11, color: "var(--mu)" }}>· {brandName}</span>
          ) : null}
          {meta ? (
            <span
              style={{
                fontSize: 10,
                padding: "2px 8px",
                borderRadius: 999,
                background: `${meta.color}22`,
                color: meta.color,
                fontWeight: 500,
              }}
            >
              {tr ? meta.tr : meta.en}
            </span>
          ) : null}
          {post.scheduled_for ? (
            <span className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
              {formatDateTime(post.scheduled_for, tr)}
            </span>
          ) : !post.published_at ? (
            // Undated draft — approving a generation without picking a slot
            // leaves scheduled_for NULL. Say so out loud instead of showing
            // a row with no date, which reads like a normal scheduled post.
            <>
              <span
                style={{
                  fontSize: 10,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "rgba(148,163,184,0.16)",
                  color: "#94A3B8",
                  fontWeight: 500,
                  border: "1px dashed rgba(148,163,184,0.5)",
                }}
                title={
                  tr
                    ? "Saati seçilmedi — 'Planla'ya bas ya da Takvim'deki 'Zamanlanmamış' bölümünden bir güne sürükle."
                    : "No time picked yet — hit Schedule, or drag it onto a day from the Calendar's Unscheduled tray."
                }
              >
                {tr ? "Zamanlanmadı" : "Unscheduled"}
              </span>
              {post.created_at ? (
                <span className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                  {tr ? "üretildi" : "created"} {formatDateTime(post.created_at, tr)}
                </span>
              ) : null}
            </>
          ) : null}
          {post.published_at ? (
            <span className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
              ✓ {formatDateTime(post.published_at, tr)}
            </span>
          ) : null}
        </div>
        <div
          onClick={onOpen}
          style={{
            fontSize: 13,
            color: "var(--tx)",
            lineHeight: 1.5,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            cursor: "pointer",
          }}
          title={post.text_body}
        >
          {post.text_body || (tr ? "(başlık yok)" : "(no caption)")}
        </div>
        {post.failure_reason ? (
          <div
            style={{
              fontSize: 11,
              color: "var(--error)",
              background: "rgba(239,68,68,0.08)",
              padding: "4px 8px",
              borderRadius: 4,
            }}
          >
            ✗ {post.failure_reason}
          </div>
        ) : null}
        {scheduling ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            <input
              type="datetime-local"
              className="input"
              value={whenLocal}
              onChange={(e) => setWhenLocal(e.target.value)}
              style={{ fontSize: 12, padding: "4px 8px", width: 200 }}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!whenLocal || busy}
              onClick={() => {
                const iso = new Date(whenLocal).toISOString();
                onSchedule(iso);
                setScheduling(false);
              }}
            >
              {tr ? "Kaydet" : "Save"}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setScheduling(false)}
            >
              {tr ? "İptal" : "Cancel"}
            </button>
          </div>
        ) : null}
      </div>

      {/* Actions */}
      {!scheduling ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems: "flex-end",
            justifyContent: "center",
          }}
        >
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onOpen}
            title={tr ? "Aç" : "Open"}
          >
            <Icons.ArrowRight size={12} /> {tr ? "Aç" : "Open"}
          </button>
          {canSchedule ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setScheduling(true)}
              disabled={busy}
            >
              {post.scheduled_for
                ? tr
                  ? "Yeni saat"
                  : "Reschedule"
                : tr
                  ? "Planla"
                  : "Schedule"}
            </button>
          ) : null}
          {canCancel ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onCancel}
              disabled={busy}
              title={tr ? "Kuyruktan çıkar" : "Remove from queue"}
            >
              {tr ? "Kuyruktan çıkar" : "Cancel"}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onDelete}
            disabled={busy}
            style={{ color: "var(--error)" }}
            title={tr ? "Sil" : "Delete"}
          >
            <Icons.X size={12} /> {tr ? "Sil" : "Delete"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// Sort key matching the backend's COALESCE(scheduled_for, created_at):
// an unscheduled draft is ranked by when it was generated, so it lands
// next to the work the operator did at the same moment.
function postSortKey(p: Post): string {
  return p.scheduled_for || p.created_at || p.published_at || "";
}

// Nearest tidy 15-min slot in the future, returned as an ISO string.
// Used as the default schedule time for unscheduled drafts so the
// operator can hit "Save" without picking a date/time first.
function nextScheduleSlotISO(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 15);
  const rounded = Math.ceil(d.getMinutes() / 15) * 15;
  if (rounded === 60) {
    d.setHours(d.getHours() + 1);
    d.setMinutes(0);
  } else {
    d.setMinutes(rounded);
  }
  return d.toISOString();
}

function toLocalDatetimeInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateTime(iso: string, tr: boolean): string {
  try {
    return new Date(iso).toLocaleString(tr ? "tr-TR" : "en-US", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
